/**
 * Settings, one part at a time (option A, approved 2026-10-29, outputs/settings-colors-team-mockup-20261029.html):
 * a tab for each part (Account, Vault and each add-on), then a short list of that part's pages.
 * Opening Settings from an app lands on that app's tab. Pages only platform admins use sit at
 * the bottom of the list under their own label.
 */
import { Navigate, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "../../lib/store";

export type Part = "account" | "vault" | "clienthub" | "reviewhub" | "vendorhub";
const PART_LABEL: Record<Part, string> = { account: "Account", vault: "Vault", clienthub: "TechHub", reviewhub: "ReviewHub", vendorhub: "VendorHub" };
const PART_SUB: Record<Part, string> = { account: "Shared", vault: "Documents", clienthub: "Add-on", reviewhub: "Add-on", vendorhub: "Add-on" };

export interface SettingsItem { to: string; label: string; part: Part; show: boolean; pa?: boolean }

export function useSettingsItems(): SettingsItem[] {
  const { me, p } = useStore();
  const pa = me.user.is_platform_admin;
  return [
    { to: "/settings/companies", label: "Companies", part: "account", show: p.anyAdmin },
    { to: "/settings/people", label: "Team", part: "account", show: p.anyAdmin },
    { to: "/settings/audit", label: "Audit log", part: "account", show: p.anyAdmin },
    { to: "/settings/ai", label: "AI", part: "account", show: p.anyAdmin },
    { to: "/settings/approvals", label: "Reviews & approvals", part: "vault", show: p.anyAdmin },
    { to: "/settings/workflows", label: "Workflows", part: "vault", show: p.anyAdmin },
    { to: "/settings/acronyms", label: "Search acronyms", part: "vault", show: true },
    { to: "/settings/codes", label: "Classification codes", part: "vault", show: pa, pa: true },
    { to: "/settings/publishing", label: "Publishing keys", part: "vault", show: pa, pa: true },
    { to: "/settings/clienthub", label: "TechHub access", part: "clienthub", show: p.anyAdmin },
    { to: "/settings/services", label: "Services", part: "clienthub", show: p.chCodes.some((c) => p.ch(c).sensitive) },
    { to: "/settings/agreement-types", label: "Agreement types", part: "clienthub", show: p.chCodes.some((c) => p.ch(c).sensitive) },
    { to: "/settings/integrations", label: "Integrations", part: "clienthub", show: p.chCodes.some((c) => p.ch(c).sensitive) },
    { to: "/settings/checklists", label: "Checklists", part: "clienthub", show: p.chCodes.some((c) => p.ch(c).sensitive) },
    { to: "/settings/reviewhub", label: "ReviewHub access", part: "reviewhub", show: p.anyAdmin },
    { to: "/settings/reviewhub/ratings", label: "Rating definitions", part: "reviewhub", show: p.rhCodes.some((c) => p.rh(c).admin) },
    { to: "/settings/reviewhub/reminders", label: "Reminders and alerts", part: "reviewhub", show: p.rhCodes.some((c) => p.rh(c).admin) },
    { to: "/settings/reviewhub/page", label: "Client page", part: "reviewhub", show: p.rhCodes.some((c) => p.rh(c).admin) },
    { to: "/settings/vendorhub", label: "VendorHub access", part: "vendorhub", show: p.anyAdmin },
    { to: "/settings/vendorhub/rules", label: "Evidence and review rules", part: "vendorhub", show: p.vhCodes.some((c) => p.vh(c).view) },
  ];
}

/** The small part tag shown above every settings page title (hidden inside Settings, where the tab says it). */
export function PartTag({ part, extra }: { part: Part; extra?: string }) {
  return (
    <div className="eyebrow part-eyebrow">
      <span className={`parttag pt-${part}`}>{PART_LABEL[part]}</span>{extra ? <span>{extra}</span> : null}
    </div>
  );
}

const PARTS: Part[] = ["account", "vault", "clienthub", "reviewhub", "vendorhub"];

export function SettingsLayout() {
  const items = useSettingsItems().filter((i) => i.show);
  const loc = useLocation();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const parts = PARTS.filter((pt) => items.some((i) => i.part === pt));
  const first = (pt: Part) => items.find((i) => i.part === pt)?.to;
  if (loc.pathname === "/settings" || loc.pathname === "/settings/") {
    // Opened from an app: land on that app's tab (Shell passes ?part=).
    const want = sp.get("part") as Part | null;
    return <Navigate to={(want && first(want)) || items[0]?.to || "/profile"} replace />;
  }
  // The longest matching path wins, so /settings/reviewhub/ratings isn't read as /settings/reviewhub.
  const cur = [...items].sort((a, b) => b.to.length - a.to.length).find((i) => loc.pathname === i.to || loc.pathname.startsWith(`${i.to}/`));
  const part: Part = cur?.part ?? parts[0] ?? "account";
  const mine = items.filter((i) => i.part === part);
  const plain = mine.filter((i) => !i.pa), paOnly = mine.filter((i) => i.pa);
  const link = (i: SettingsItem) => <NavLink key={i.to} to={i.to} end className={({ isActive }) => `g-${part}${isActive ? " on" : ""}`}>{i.label}</NavLink>;
  return (
    <div className="set-wrap">
      <div className="stabs" role="tablist" aria-label="Settings for">
        {parts.map((pt) => (
          <button key={pt} role="tab" aria-selected={pt === part} className={`stab st-${pt} ${pt === part ? "on" : ""}`} onClick={() => { const to = first(pt); if (to) nav(to); }}>
            {PART_LABEL[pt]}{pt !== "account" && pt !== "vault" && <small>{PART_SUB[pt]}</small>}
          </button>
        ))}
      </div>
      <div className="set-layout">
        <nav className="snav" aria-label={`${PART_LABEL[part]} settings`}>
          {plain.map(link)}
          {paOnly.length > 0 && <><div className="snav-cap">Platform admins</div>{paOnly.map(link)}</>}
        </nav>
        <div className="set-main"><Outlet /></div>
      </div>
    </div>
  );
}
